import { useSyncExternalStore } from 'react';

/**
 * Client-side demo mode for DBO v2. When active, the org-level instance
 * list endpoint is served from the demo dataset (the mock middleware reads
 * this flag), so end users can explore the full overview experience —
 * onboarded instances, dashboards and the "Not onboarded" list — without
 * any real data. Persists for the browser session.
 */

const STORAGE_KEY = 'dbo-demo-mode';

const readStored = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

let active = readStored();
const listeners = new Set<() => void>();

/** Read outside React — used by the mock middleware. */
export const isDboDemoModeActive = (): boolean => active;

export const setDboDemoMode = (value: boolean): void => {
  active = value;
  if (typeof window !== 'undefined') {
    try {
      if (value) window.sessionStorage.setItem(STORAGE_KEY, '1');
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Non-persistent environment — the flag stays in-memory only.
    }
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** React binding — re-renders when demo mode is toggled. */
export const useDboDemoMode = (): boolean =>
  useSyncExternalStore(subscribe, isDboDemoModeActive);
